import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { i18n } from '@/utils/i18n';

import {
  acceptTermsAndConditionsNpo,
  createNpoPaymentAccountDocuments,
  createNpoPaymentAccountSteps,
  deleteAchCheckIndividual,
  deleteCreditCardIndividual,
  deleteIndividualCustomer,
  deleteSubscription,
  deleteTransactionIndividual,
  editSubscription,
  getCreditCardInfoIndividual,
  getListPaymentMethods,
  getNpoPaymentAccountSteps,
  getSubscriptionsListScrolling,
  getTermsAndConditionsCreateAccountNpo,
  getTransactionsListScrolling,
  getTransactionHistoryPdf,
  getTransactionHistoryCsv,
  individualAddAchCheck,
  individualAddCreditCard,
  partialEditAchCheckIndividual,
  partialEditCreditCardIndividual,
  refundTransactionNPO,
  saveStepsNpoPaymentAccount,
  validateAchCheck
} from '../functions/paymentAccounts';
import { SubscriptionsListParams, TransactionsListParams } from '../models/paymentAccountsModels';

// NPO Hooks
export const useGetTermsCreateNpo = () => {
  return useMutation({
    mutationKey: ['get-terms-create-npo'],
    mutationFn: getTermsAndConditionsCreateAccountNpo,
    onError: (e: any) => {
      console.error('Error getting terms and conditions', e);
    }
  });
};

export const useAcceptTermsNpo = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['accept-terms-npo'],
    mutationFn: acceptTermsAndConditionsNpo,
    onError: (e: any) => {
      showError({});
      console.error('Error accepting terms and conditions', e);
    }
  });
};

export const useGetNpoPaymentAccountSteps = (npoId: string) =>
  useQuery({
    queryKey: ['get-npo-payment-account-steps'],
    queryFn: () => getNpoPaymentAccountSteps({ npoId }),
    enabled: !!npoId
  });

export const useCreateNpoPaymentAccountSteps = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['create-npo-payment-account-steps'],
    mutationFn: createNpoPaymentAccountSteps,
    onError: (e: AxiosError) => {
      const errorData = e.response?.data as { message: string };

      if (e.status === 400) {
        const rawError = (e.response?.data as any).message;

        if (typeof rawError === 'string') {
          try {
            const parsed = JSON.parse(rawError);

            const errorDetail = parsed?.response?.details?.Message ?? parsed?.response?.details?.message;
            const errorDetailMessage = (JSON.parse(errorDetail) as string[])?.join('\n') ?? errorDetail;

            const msg = i18n.t('npoPaymentOnboarding.api_error', { ERROR_DETAIL: errorDetailMessage });

            showError({
              title: e.message,
              message: msg
            });
            console.error('Error creating npo payment account steps', e);
          } catch {
            showError({
              title: e.message,
              message: errorData?.message
            });
            console.error('Error creating npo payment account steps', e);
          }
        }
      } else {
        showError({
          title: e.message,
          message: errorData?.message
        });
        console.error('Error creating npo payment account steps', e);
      }
    }
  });
};

export const useSaveNpoPaymentAccountSteps = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['save-npo-payment-account-steps'],
    mutationFn: saveStepsNpoPaymentAccount,
    onError: (e: AxiosError) => {
      try {
        const status = e.response?.status;
        const data = e.response?.data;
        let message: string | undefined;

        // Case 1: BE returns object with details
        if (data && typeof data === 'object') {
          const details = (data as any).details;
          if (details) {
            const rawMessage = details.Message ?? details.message;

            if (typeof rawMessage === 'string') {
              try {
                // Try to parse string into JSON (might be array)
                const parsedDetail = JSON.parse(rawMessage);
                if (Array.isArray(parsedDetail)) {
                  message = parsedDetail.join('\n');
                } else {
                  message = String(parsedDetail);
                }
              } catch {
                // Not JSON → just use the string
                message = rawMessage;
              }
            } else if (Array.isArray(rawMessage)) {
              message = rawMessage.join('\n');
            } else if (rawMessage) {
              message = String(rawMessage);
            }
          }

          // fallback if BE uses `message` at root level
          if (!message && 'message' in data) {
            message = (data as any).message;
          }
        }

        // Case 2: BE returns raw string (could be JSON or HTML)
        if (!message && typeof data === 'string') {
          try {
            const parsed = JSON.parse(data);
            message = parsed.message ?? data;
          } catch {
            message = data.replace(/<\/?[^>]+(>|$)/g, '').trim() || 'Unknown error';
          }
        }

        // Fallback
        if (!message) {
          message = 'Unexpected server error';
        }

        showError({
          title: e.message || `Error ${status}`,
          message: i18n.t('npoPaymentOnboarding.api_error', { ERROR_DETAIL: message })
        });

        console.error('Error creating npo payment account steps', e, { parsedMessage: message });
      } catch (unexpected) {
        showError({
          title: 'Unexpected error',
          message: 'Something went wrong while processing the error response.'
        });
        console.error('Error handling failed error', unexpected, e);
      }
    }
  });
};

export const useCreateNpoPaymentAccountDocuments = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['create-npo-payment-account-documents'],
    mutationFn: createNpoPaymentAccountDocuments,
    onError: (e: AxiosError) => {
      const errorData = e.response?.data as { message: string };

      if (e.status === 400) {
        const rawError = (e.response?.data as any).message;

        if (typeof rawError === 'string') {
          try {
            const parsed = JSON.parse(rawError);

            const errorDetail = parsed?.response?.details?.Message ?? parsed?.response?.details?.message;
            const errorDetailMessage = (JSON.parse(errorDetail) as string[])?.join('\n') ?? errorDetail;

            const msg = i18n.t('npoPaymentOnboarding.api_error', { ERROR_DETAIL: errorDetailMessage });

            showError({
              title: e.message,
              message: msg
            });
            console.error('Error creating npo payment account', e);
          } catch {
            showError({
              title: e.message,
              message: errorData?.message
            });
            console.error('Error creating npo payment account', e);
          }
        }
      } else {
        showError({
          title: e.message,
          message: errorData?.message
        });
        console.error('Error creating npo payment account', e);
      }
    }
  });
};

// Individual Hooks
export const useGetPaymentMethodList = (npoId: string) =>
  useQuery({
    queryKey: ['get-payment-method-list'],
    queryFn: () => getListPaymentMethods({ npoId }),
    enabled: !!npoId
  });

export const useGetCreditCardInfo = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['get-credit-card-info'],
    mutationFn: getCreditCardInfoIndividual,
    onError: (e: any) => {
      console.error('Error getting credit card info');
      showError({});
    }
  });
};

export const useDeleteIndividualCustomer = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['delete-individual-customer'],
    mutationFn: deleteIndividualCustomer,
    onError: (e: any) => {
      console.error('Error deleting individual customer');
      showError({});
    }
  });
};

// CreditCard
export const useAddCreditCard = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['add-credit-card'],
    mutationFn: individualAddCreditCard,
    onError: (e: any) => {
      showError({});
    }
  });
};

export const useEditCreditCard = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['partial-edit-credit-card'],
    mutationFn: partialEditCreditCardIndividual,
    onError: (e: any) => {
      showError({});
      console.error('Error editing credit card', e);
    }
  });
};

export const useDeleteCreditCard = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['delete-credit-card'],
    mutationFn: deleteCreditCardIndividual,
    onError: (e: any) => {
      showError({});
      console.error('Error deleting credit card');
    }
  });
};

// AchAccounts
export const useAddAchCheck = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['add-ach-check'],
    mutationFn: individualAddAchCheck,
    onError: (e: any) => {
      showError({});
      console.error('Error adding ach check', e);
    }
  });
};

export const useEditAchCheck = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['partial-edit-ach-check'],
    mutationFn: partialEditAchCheckIndividual,
    onError: (e: any) => {
      showError({});
      console.error('Error editing ach check', e);
    }
  });
};

export const useDeleteAchCheck = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['delete-ach-check'],
    mutationFn: deleteAchCheckIndividual,
    onError: (e: any) => {
      showError({});
      console.error('Error deleting ach check', e);
    }
  });
};

export const useValidateAchCheck = () =>
  useMutation({
    mutationKey: ['validate-ach-check'],
    mutationFn: validateAchCheck
  });

// Subscriptions
export const useGetSubscriptionListScrolling = (params: SubscriptionsListParams) =>
  useInfiniteQuery({
    queryKey: ['get-subscriptions-list', params.n, params.limit],
    queryFn: getSubscriptionsListScrolling,
    initialPageParam: '0',
    getNextPageParam: (lastPage) => {
      if (lastPage.last) {
        return lastPage.last.toString();
      } else {
        return null;
      }
    }
  });

export const useEditSubscription = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['edit-subscription'],
    mutationFn: editSubscription,
    onError: (e: any) => {
      showError({});
      console.error('Error editing subscription', e);
    }
  });
};

export const useDeleteSubscription = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['delete-subscription'],
    mutationFn: deleteSubscription,
    onError: (e: any) => {
      showError({});
      console.error('Error deleting subscription');
    }
  });
};

// Transactions
export const useGetTransactionListScrolling = (params: TransactionsListParams) =>
  useInfiniteQuery({
    queryKey: ['get-transactions-list', params.n, params.limit],
    queryFn: getTransactionsListScrolling,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.last) {
        return lastPage.last.toString();
      } else {
        return null;
      }
    }
  });

export const useRefundTransactionNPO = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['refund-transaction-npo'],
    mutationFn: refundTransactionNPO,
    onError: (e: any) => {
      showError({});
      console.error('Error refunding transaction', e);
    }
  });
};

export const useDeleteTransactionIndividual = () =>
  useMutation({
    mutationKey: ['delete-transaction-individual'],
    mutationFn: deleteTransactionIndividual,
    onError: (e: AxiosError) => {
      console.error('Error deleting transaction', e);
    }
  });

export const useGetTransactionHistoryPdf = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['get-transaction-history-pdf'],
    mutationFn: getTransactionHistoryPdf,
    onError: (e: any) => {
      showError({});
      console.error('Error getting transaction history pdf', e);
    }
  });
};

export const useGetTransactionHistoryCsv = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['get-transaction-history-csv'],
    mutationFn: getTransactionHistoryCsv,
    onError: (e: any) => {
      showError({});
      console.error('Error getting transaction history csv', e);
    }
  });
};
